import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useUser } from '@clerk/clerk-react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft, 
  FileText, 
  Book, 
  Download, 
  Copy, 
  Check, 
  Upload, 
  Clock, 
  RefreshCw,
  Rocket,
  Eye,
  Lightbulb,
  AlertTriangle,
  Info
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import EmbeddedLecture from '@/components/lectures/EmbeddedLecture';
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';

interface Lecture {
  id: string;
  title: string;
  version: string;
  lastUpdated: string;
  embedUrl: string;
}

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Evidence Box component
const EvidenceBox = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 my-6">
      <div className="flex items-center mb-2">
        <Info className="text-gray-600 h-5 w-5 mr-2" />
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-gray-700 text-sm">
        {children}
      </div>
    </div>
  );
};

// Simple Diagram component for generated lectures
const SimpleDiagram = ({ type, data }: { type: string, data: any }) => {
  // Basic diagram types that can be generated
  if (type === 'flow') {
    return (
      <div className="my-6 flex justify-center">
        <svg width="600" height="200" viewBox="0 0 600 200" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
            </filter>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
            </marker>
          </defs>
          
          {/* Background */}
          <rect width="600" height="200" fill="#f8fafc" rx="5" ry="5" />
          
          {/* Flow boxes */}
          {data.nodes?.map((node: any, idx: number) => (
            <g key={idx}>
              <rect 
                x={node.x} 
                y={node.y} 
                width={node.width || 120} 
                height={node.height || 60} 
                rx="10" 
                ry="10" 
                fill={node.color || "#e0e7ff"} 
                stroke={node.borderColor || "#6366f1"} 
                strokeWidth="2" 
                filter="url(#shadow)" 
              />
              <text 
                x={node.x + (node.width || 120) / 2} 
                y={node.y + (node.height || 60) / 2} 
                textAnchor="middle" 
                alignmentBaseline="middle" 
                fontFamily="Arial" 
                fontSize="14" 
                fontWeight="bold" 
                fill={node.textColor || "#4338ca"}
              >
                {node.label}
              </text>
            </g>
          ))}
          
          {/* Arrows */}
          {data.connections?.map((conn: any, idx: number) => (
            <line 
              key={idx}
              x1={conn.x1} 
              y1={conn.y1} 
              x2={conn.x2} 
              y2={conn.y2} 
              stroke="#64748b" 
              strokeWidth="2" 
              markerEnd="url(#arrowhead)" 
            />
          ))}
        </svg>
      </div>
    );
  }
  
  // Default: simple box diagram
  return (
    <div className="my-6 bg-gray-100 p-8 rounded-lg text-center">
      <p className="text-gray-600">Diagram: {type}</p>
    </div>
  );
};

const LectureStudio = () => {
  const [, navigate] = useLocation();
  const { user } = useUser();
  const { toast } = useToast();
  
  // Generator state
  const [lectureId, setLectureId] = useState('');
  const [lectureTitle, setLectureTitle] = useState('');
  const [lectureDescription, setLectureDescription] = useState('');
  const [slideContent, setSlideContent] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  
  // Manager state
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [isLoadingLectures, setIsLoadingLectures] = useState(true);
  const [selectedLecture, setSelectedLecture] = useState<Lecture | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSlides, setPreviewSlides] = useState<SlideContent[]>([]);

  // Check if user is authorized
  const isAuthorized = user?.emailAddresses?.[0]?.emailAddress === 'anant@nextgenerationmedicine.co';

  useEffect(() => {
    if (isAuthorized) {
      fetchLectures();
    }
  }, [isAuthorized]);

  const fetchLectures = async () => {
    try {
      const response = await apiRequest('GET', '/api/lectures');
      const data = await response.json();
      setLectures(data.lectures || []);
    } catch (error) {
      console.error('Failed to fetch lectures:', error);
    } finally {
      setIsLoadingLectures(false);
    }
  };

  const generateUniqueId = () => {
    return `lecture-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const parseSlideContent = (content: string) => {
    const slides: SlideContent[] = [];
    const slideBlocks = content.split(/^#{1,2}\s+/m).filter(block => block.trim());
    
    slideBlocks.forEach((block, index) => {
      const lines = block.trim().split('\n');
      const title = lines[0].replace(/^\d+\.\s*/, '');
      const contentLines = lines.slice(1).join('\n').trim();
      
      if (title && contentLines) {
        slides.push({
          id: index + 1,
          title: title,
          content: parseContentToReact(contentLines)
        });
      }
    });
    
    return slides;
  };

  const parseContentToReact = (content: string): React.ReactNode => {
    const elements: React.ReactNode[] = [];
    const lines = content.split('\n');
    let currentList: string[] = [];
    let inList = false;
    let inDiagram = false;
    let diagramLines: string[] = [];
    
    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-4">
            {currentList.map((item, idx) => (
              <li key={idx} dangerouslySetInnerHTML={{ __html: formatText(item) }} />
            ))}
          </ul>
        );
        currentList = [];
        inList = false;
      }
    };
    
    const processDiagram = () => {
      if (diagramLines.length > 0) {
        try {
          const diagramData = JSON.parse(diagramLines.join('\n'));
          elements.push(
            <SimpleDiagram key={`diagram-${elements.length}`} type={diagramData.type} data={diagramData} />
          );
        } catch (e) {
          // If parsing fails, show as code block
          elements.push(
            <pre key={`diagram-error-${elements.length}`} className="bg-gray-100 p-4 rounded my-4 overflow-x-auto">
              <code>{diagramLines.join('\n')}</code>
            </pre>
          );
        }
        diagramLines = [];
        inDiagram = false;
      }
    };
    
    const formatText = (text: string): string => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
    };
    
    lines.forEach((line, idx) => {
      const trimmedLine = line.trim();
      
      if (trimmedLine === '```diagram') {
        flushList();
        inDiagram = true;
      } else if (trimmedLine === '```' && inDiagram) {
        processDiagram();
      } else if (inDiagram) {
        diagramLines.push(line);
      } else if (trimmedLine.startsWith('**KeyTakeaway:**')) {
        flushList();
        const content = trimmedLine.replace('**KeyTakeaway:**', '').trim();
        elements.push(
          <KeyTakeaway key={`takeaway-${idx}`}>
            <span dangerouslySetInnerHTML={{ __html: formatText(content) }} />
          </KeyTakeaway>
        );
      } else if (trimmedLine.startsWith('**ClinicalNote:**')) {
        flushList();
        const content = trimmedLine.replace('**ClinicalNote:**', '').trim();
        elements.push(
          <ClinicalNote key={`clinical-${idx}`}>
            <span dangerouslySetInnerHTML={{ __html: formatText(content) }} />
          </ClinicalNote>
        );
      } else if (trimmedLine.startsWith('**Evidence:**')) {
        flushList();
        const content = trimmedLine.replace('**Evidence:**', '').trim();
        elements.push(
          <EvidenceBox key={`evidence-${idx}`} title="Evidence">
            <span dangerouslySetInnerHTML={{ __html: formatText(content) }} />
          </EvidenceBox>
        );
      } else if (trimmedLine.startsWith('- ')) {
        inList = true;
        currentList.push(trimmedLine.substring(2));
      } else if (trimmedLine && !inList) {
        flushList();
        elements.push(
          <p key={`p-${idx}`} className="mb-4" dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
        );
      } else if (trimmedLine && inList) {
        // Continue list item on new line
        if (currentList.length > 0) {
          currentList[currentList.length - 1] += ' ' + trimmedLine;
        }
      } else if (!trimmedLine && inList) {
        flushList();
      }
    });
    
    flushList();
    processDiagram();
    
    return <>{elements}</>;
  };

  const parseContentToHTML = (content: React.ReactNode): string => {
    // Convert React content back to HTML for static generation
    const lines: string[] = [];
    
    if (React.isValidElement(content)) {
      React.Children.forEach(content.props.children, (child) => {
        if (React.isValidElement(child)) {
          const type = child.type;
          const props = child.props;
          
          if (type === KeyTakeaway) {
            lines.push(`<div class="key-takeaway">\n  <strong>Key Takeaway:</strong> ${props.children?.props?.dangerouslySetInnerHTML?.__html || ''}\n</div>`);
          } else if (type === ClinicalNote) {
            lines.push(`<div class="clinical-note">\n  <strong>Clinical Note:</strong> ${props.children?.props?.dangerouslySetInnerHTML?.__html || ''}\n</div>`);
          } else if (type === EvidenceBox) {
            lines.push(`<div class="evidence-box">\n  <strong>Evidence:</strong> ${props.children?.props?.dangerouslySetInnerHTML?.__html || ''}\n</div>`);
          } else if (type === SimpleDiagram) {
            // Convert diagram to static SVG HTML
            lines.push(`<div class="diagram-container" style="display: flex; justify-content: center; margin: 1.5rem 0;">
              <svg width="600" height="200" viewBox="0 0 600 200" xmlns="http://www.w3.org/2000/svg">
                <rect width="600" height="200" fill="#f8fafc" rx="5" ry="5" />
                <text x="300" y="100" text-anchor="middle" font-family="Arial" font-size="14" fill="#64748b">
                  [Diagram: ${props.type || 'flow'}]
                </text>
              </svg>
            </div>`);
          } else if (child.type === 'p') {
            lines.push(`<p>${props.dangerouslySetInnerHTML?.__html || ''}</p>`);
          } else if (child.type === 'ul') {
            lines.push('<ul>');
            React.Children.forEach(props.children, (li) => {
              if (React.isValidElement(li) && li.type === 'li') {
                lines.push(`  <li>${li.props.dangerouslySetInnerHTML?.__html || ''}</li>`);
              }
            });
            lines.push('</ul>');
          } else if (child.type === 'pre') {
            lines.push(`<pre class="code-block">${props.children?.props?.children || ''}</pre>`);
          }
        }
      });
    }
    
    return lines.join('\n');
  };

  const generateStaticHTML = (title: string, description: string, slides: Array<{id: number, title: string, content: React.ReactNode}>, lectureId: string) => {
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      background: #f5f5f5;
      padding: 20px;
    }
    
    .container {
      max-width: 900px;
      margin: 0 auto;
    }
    
    h1 {
      color: #2C3E50;
      font-size: 2.5rem;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }
    
    .description {
      color: #7F8C8D;
      font-size: 1.1rem;
      margin-bottom: 2rem;
    }
    
    .slide {
      background: white;
      border-radius: 8px;
      padding: 2rem;
      margin-bottom: 1.5rem;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      transition: box-shadow 0.3s ease;
    }
    
    .slide:hover {
      box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }
    
    .slide h2 {
      color: #2C3E50;
      margin-bottom: 1.5rem;
      font-size: 1.75rem;
      font-weight: 600;
    }
    
    .slide p {
      margin-bottom: 1rem;
      line-height: 1.8;
    }
    
    .slide ul {
      margin: 1rem 0;
      padding-left: 1.5rem;
    }
    
    .slide li {
      margin-bottom: 0.5rem;
    }
    
    .key-takeaway {
      background: #EBF8FF;
      border-left: 4px solid #3182CE;
      padding: 1rem;
      margin: 1.5rem 0;
      border-radius: 0 4px 4px 0;
    }
    
    .key-takeaway strong {
      color: #2B6CB0;
    }
    
    .clinical-note {
      background: #FFFBEB;
      border-left: 4px solid #F59E0B;
      padding: 1rem;
      margin: 1.5rem 0;
      border-radius: 0 4px 4px 0;
    }
    
    .clinical-note strong {
      color: #D97706;
    }
    
    .evidence-box {
      background: #F9FAFB;
      border: 1px solid #E5E7EB;
      padding: 1rem;
      margin: 1.5rem 0;
      border-radius: 4px;
    }
    
    .evidence-box strong {
      color: #4B5563;
    }
    
    .code-block, pre {
      background: #f3f4f6;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      padding: 1rem;
      margin: 1.5rem 0;
      overflow-x: auto;
      font-family: monospace;
      font-size: 0.875rem;
    }
    
    .diagram-container {
      display: flex;
      justify-content: center;
      margin: 1.5rem 0;
    }
    
    .metadata {
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(255,255,255,0.95);
      padding: 0.75rem;
      border-radius: 4px;
      font-size: 0.75rem;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      font-family: monospace;
    }
    
    .metadata div {
      margin-bottom: 0.25rem;
    }
    
    @media (max-width: 768px) {
      body {
        padding: 10px;
      }
      
      h1 {
        font-size: 2rem;
      }
      
      .slide {
        padding: 1.5rem;
      }
      
      .metadata {
        display: none;
      }
    }
  </style>
</head>
<body>
  <div class="metadata">
    <div><strong>ID:</strong> ${lectureId}</div>
    <div><strong>Generated:</strong> ${new Date().toISOString()}</div>
    <div><strong>Version:</strong> 1.0.0</div>
  </div>
  
  <div class="container">
    <h1>${title}</h1>
    <p class="description">${description || 'A comprehensive exploration of key concepts and clinical applications.'}</p>
    
    ${slides.map((slide, index) => `
    <div class="slide" data-slide-id="${slide.id}" data-slide-index="${index}">
      <h2>Slide ${index + 1}: ${slide.title}</h2>
      ${parseContentToHTML(slide.content)}
    </div>
    `).join('')}
  </div>
  
  <script>
    // Send height to parent for iframe resizing
    function sendHeight() {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ 
        type: 'resize', 
        height: height,
        lectureId: '${lectureId}'
      }, '*');
    }
    
    // Send height on load and resize
    window.addEventListener('load', sendHeight);
    window.addEventListener('resize', sendHeight);
    
    // Track which slides are viewed
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          window.parent.postMessage({
            type: 'slideViewed',
            slideId: entry.target.dataset.slideId,
            slideIndex: entry.target.dataset.slideIndex,
            lectureId: '${lectureId}'
          }, '*');
        }
      });
    }, { threshold: 0.5 });
    
    document.querySelectorAll('.slide').forEach(slide => {
      observer.observe(slide);
    });
    
    // Send ready message
    window.parent.postMessage({ 
      type: 'ready',
      lectureId: '${lectureId}'
    }, '*');
  </script>
</body>
</html>`;
    
    return htmlContent;
  };

  const handlePublish = async () => {
    if (!lectureId || !lectureTitle || !slideContent) {
      toast({
        title: "Missing Information",
        description: "Please provide lecture ID, title, and slide content.",
        variant: "destructive"
      });
      return;
    }

    setIsPublishing(true);
    
    try {
      // Parse slides and generate HTML
      const slides = parseSlideContent(slideContent);
      const htmlContent = generateStaticHTML(lectureTitle, lectureDescription, slides, lectureId);
      
      // Upload directly to server
      const response = await apiRequest('POST', '/api/lectures/store', {
        lectureId,
        title: lectureTitle,
        htmlContent
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: `Lecture published successfully. Version: ${data.version}`
        });
        
        // Clear form
        setLectureId('');
        setLectureTitle('');
        setLectureDescription('');
        setSlideContent('');
        
        // Refresh lectures list
        fetchLectures();
        
        // Switch to manage tab
        // Find the newly created/updated lecture and select it
        const updatedLecture = {
          id: lectureId,
          title: lectureTitle,
          version: data.version,
          lastUpdated: new Date().toISOString(),
          embedUrl: data.embedUrl
        };
        setSelectedLecture(updatedLecture);
      }
    } catch (error: any) {
      toast({
        title: "Publish Failed",
        description: error.message || "Failed to publish lecture",
        variant: "destructive"
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditLecture = (lecture: Lecture) => {
    // Load lecture data into the generator
    setLectureId(lecture.id);
    setLectureTitle(lecture.title);
    // Note: We don't have the original markdown, so user will need to provide it
    setSlideContent('');
    toast({
      title: "Editing Mode",
      description: "Loaded lecture details. Please provide the updated slide content.",
    });
  };

  if (!isAuthorized) {
    return (
      <MentorshipAccessGate>
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h1>
              <p className="text-gray-600">This tool is only available to anant@nextgenerationmedicine.co</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/mentorship")}
              >
                Back to Mentorship
              </Button>
            </div>
          </div>
          <Footer />
        </div>
      </MentorshipAccessGate>
    );
  }

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
          <div className="flex items-center justify-between mb-6">
            <Button 
              variant="ghost" 
              className="flex items-center text-blue-600"
              onClick={() => navigate("/mentorship")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Mentorship
            </Button>
          </div>
          
          <div>
            {/* Breadcrumb Navigation */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Mentorship</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-gray-600">Lecture Studio</span>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
              Lecture Studio
            </h1>
            <p className="text-[#7F8C8D] mb-8">
              Create, publish, and manage embedded HTML lectures all in one place.
            </p>
            
            <Tabs defaultValue="create" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="create">
                  <Rocket className="mr-2 h-4 w-4" />
                  Create & Publish
                </TabsTrigger>
                <TabsTrigger value="manage">
                  <FileText className="mr-2 h-4 w-4" />
                  Manage Lectures
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="create" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Create New Lecture</CardTitle>
                    <CardDescription>
                      Write your content in markdown and publish directly as an embedded HTML lecture
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="lectureId">Lecture ID</Label>
                        <Input
                          id="lectureId"
                          value={lectureId}
                          onChange={(e) => setLectureId(e.target.value)}
                          placeholder="e.g., testosterone-molecular-foundations"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Use the same ID to update existing lectures
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="title">Lecture Title</Label>
                        <Input
                          id="title"
                          value={lectureTitle}
                          onChange={(e) => setLectureTitle(e.target.value)}
                          placeholder="e.g., Testosterone: Molecular Foundations"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Input
                        id="description"
                        value={lectureDescription}
                        onChange={(e) => setLectureDescription(e.target.value)}
                        placeholder="Brief description of the lecture content"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="content">Slide Content (Markdown)</Label>
                      <Textarea
                        id="content"
                        value={slideContent}
                        onChange={(e) => setSlideContent(e.target.value)}
                        placeholder={`## Slide 1: Introduction
Introduction content here. Use **bold** for emphasis.

**KeyTakeaway:** Important points go here.

## Slide 2: Core Concepts
- Bullet point 1
- Bullet point 2

**ClinicalNote:** Clinical insights here.

**Evidence:** Research findings here.

## Slide 3: Process Flow
Here's how the process works:

\`\`\`diagram
{
  "type": "flow",
  "nodes": [
    {"x": 50, "y": 70, "label": "Start"},
    {"x": 250, "y": 70, "label": "Process"},
    {"x": 450, "y": 70, "label": "End"}
  ],
  "connections": [
    {"x1": 170, "y1": 100, "x2": 250, "y2": 100},
    {"x1": 370, "y1": 100, "x2": 450, "y2": 100}
  ]
}
\`\`\``}
                        className="min-h-[400px] font-mono text-sm"
                      />
                    </div>
                    
                    <div className="flex gap-4">
                      <Button 
                        onClick={() => {
                          const slides = parseSlideContent(slideContent);
                          setPreviewSlides(slides);
                          setIsPreviewOpen(true);
                        }}
                        variant="outline"
                        disabled={!slideContent}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Preview Slides
                      </Button>
                      
                      <Button 
                        onClick={handlePublish} 
                        disabled={isPublishing}
                        className="flex-1"
                        size="lg"
                      >
                        <Rocket className="mr-2 h-4 w-4" />
                        {isPublishing ? 'Publishing...' : 'Publish Lecture'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Preview Section */}
                {isPreviewOpen && previewSlides.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Preview</CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsPreviewOpen(false)}
                        >
                          Close Preview
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Slideshow slides={previewSlides} />
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              
              <TabsContent value="manage" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Published Lectures</CardTitle>
                    <CardDescription>
                      View and manage your published lectures
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingLectures ? (
                      <p className="text-center text-gray-500 py-8">Loading lectures...</p>
                    ) : lectures.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No lectures published yet</p>
                    ) : (
                      <div className="space-y-4">
                        {lectures.map((lecture) => (
                          <div
                            key={lecture.id}
                            className="border rounded-lg p-4 space-y-3"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium">{lecture.title}</h4>
                                <p className="text-sm text-gray-500">ID: {lecture.id}</p>
                              </div>
                              <Badge variant="outline">v{lecture.version}</Badge>
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Clock className="h-3 w-3" />
                              <span>{formatDate(lecture.lastUpdated)}</span>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedLecture(lecture);
                                  setIsPreviewOpen(true);
                                }}
                              >
                                <Eye className="mr-2 h-3 w-3" />
                                Preview
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditLecture(lecture)}
                              >
                                <FileText className="mr-2 h-3 w-3" />
                                Update
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Preview Section */}
                {selectedLecture && isPreviewOpen && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Preview: {selectedLecture.title}</CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsPreviewOpen(false)}
                        >
                          Close Preview
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <EmbeddedLecture
                        embedUrl={selectedLecture.embedUrl}
                        lectureId={selectedLecture.id}
                        title={selectedLecture.title}
                      />
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default LectureStudio; 